import {
    Badge,
    Box,
    Button,
    FileInput,
    Group,
    Modal,
    Paper,
    Select,
    SimpleGrid,
    Stack,
    Tabs,
    TagsInput,
    Text,
    TextInput,
    Textarea,
} from '@mantine/core';
import { getMediaGroups, getUserMediaGroups } from '../../../../http/apiClient';
import { useEffect, useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SearchableSelect } from '../../../common';
import { countVariations } from '../../../../utils/text';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';
import { genericValidator } from '../../../../utils/validation';
import { hasRole } from '../../../../utils/roles';
import useAddLink from '../../../../http/mutations/groups/useAddLink';
import { useAuthStore } from '../../../../store';
import { useForm } from '@mantine/form';

//  ┌──── protocol ────┐ ┌──────── host / IP ───────┐ ┌─ port ──┐ ┌─ path ─────┐ ┌──────── query (with [tokens]) ────────────┐ ┌ fragment ─┐
const URL_WITH_TOKENS_RE =
    /^(https?:\/\/)(?:(?:[A-Za-z0-9-]+\.)+[A-Za-z]{2,}|(?:\d{1,3}\.){3}\d{1,3})(?::\d{2,5})?(?:\/[^\s?#]*)?(?:\?(?:[^\s#\[\]]|\[[A-Za-z0-9_-]+\])*)?(?:#[^\s]*)?$/i;

const EditLinkModal = ({ linkGroup, existing, opened, close }) => {
    const userProfile = useAuthStore((state) => state.userProfile);
    const getInitialValues = () => {
        if (!existing) {
            return {
                id: null,
                groupId: linkGroup?.id,
                userId: hasRole(userProfile, 'SUPER_ADMIN')
                    ? userProfile?.id.toString()
                    : null,
                rawLink: '',
                trackingId: '',
                tags: [],
            };
        }

        return {
            id: existing.id,
            groupId: linkGroup?.id,
            userId: hasRole(userProfile, 'SUPER_ADMIN')
                ? existing.userId
                : null,
            rawLink: existing.rawLink,
            trackingId: existing.trackingId,
            tags: existing.tags,
        };
    };
    const form = useForm({
        validateInputOnBlur: true,
        initialValues: getInitialValues(),
        transformValues: (values) => ({
            ...values,
            trackingId: values.trackingId || null,
        }),

        validate: {
            rawLink: (value) => {
                const { type, message } = genericValidator('Link', value, {
                    required: true,
                    pattern: URL_WITH_TOKENS_RE,
                });
                if (type) {
                    return type === 'pattern' ? 'Invalid link' : message;
                }
            },

            trackingId: (value) => {
                const { type, message } = genericValidator(
                    'Tracking ID',
                    value,
                    {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    }
                );
                if (type) {
                    return type === 'pattern'
                        ? 'Tracking ID must only contain letters, numbers, hyphens and underscores'
                        : message;
                }
            },

            tags: (values) => {
                if (!values) {
                    return;
                }

                for (const value of values) {
                    const { type, message } = genericValidator('Tags', value, {
                        required: false,
                        minLength: 4,
                        maxLength: 60,
                        pattern: /^[a-zA-Z0-9_-]+$/,
                    });
                    if (type) {
                        return type === 'pattern'
                            ? 'Tags must only contain letters, numbers, hyphens and underscores'
                            : message;
                    }
                }
            },
        },
    });

    useEffect(() => {
        const initialValues = getInitialValues();
        form.setInitialValues(initialValues);
        form.reset();
    }, [existing, opened]);

    const { isPending, mutate: addOrUpdate } = useAddLink();

    const handleAddToken = (currentToken) => {
        if (!currentToken) {
            return;
        }
        const currentValue = form.getValues()['rawLink'] || '';
        form.setFieldValue('rawLink', `${currentValue}[${currentToken}]`);
    };

    const onSubmit = (model) => {
        addOrUpdate(model, {
            onSuccess(data, variables, context) {
                close();
            },
            onError(error, variables, context) {
                // setError(error);
            },
        });
    };

    return (
        <Modal.Root
            opened={opened}
            onClose={close}
            size={'md'}>
            <Modal.Overlay />
            <Modal.Content>
                <Modal.Header>
                    <Modal.Title>
                        {existing ? 'Update' : 'Add'} Link
                    </Modal.Title>
                    <Modal.CloseButton />
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={form.onSubmit(onSubmit)}>
                        <Stack gap={'xs'}>
                            <Textarea
                                autosize
                                minRows={5}
                                maxRows={20}
                                inputWrapperOrder={[
                                    'label',
                                    'input',
                                    'error',
                                    'description',
                                ]}
                                label={'Link'}
                                placeholder={'Link'}
                                key={form.key('rawLink')}
                                {...form.getInputProps('rawLink')}
                            />

                            <Select
                                label={'Add Tokens'}
                                data={[
                                    {
                                        label: 'Flush Offers',
                                        value: 'flush_offers',
                                    },
                                    {
                                        label: 'Campaign ID',
                                        value: 'campaign_id',
                                    },
                                    {
                                        label: 'Campaign Name',
                                        value: 'campaign_name',
                                    },
                                    {
                                        label: 'Message ID',
                                        value: 'message_id',
                                    },
                                    {
                                        label: 'Link ID',
                                        value: 'link_id',
                                    },
                                    {
                                        label: 'Phone Number',
                                        value: 'phone_number',
                                    },
                                ]}
                                onChange={handleAddToken}
                            />

                            <TextInput
                                label={'Tracking ID'}
                                placeholder='Enter a tracking id'
                                key={form.key('trackingId')}
                                {...form.getInputProps('trackingId')}
                            />

                            <TagsInput
                                label={'Tags'}
                                placeholder={'Tags'}
                                key={form.key('tags')}
                                {...form.getInputProps('tags')}
                            />

                            <Group justify='flex-end'>
                                <Button
                                    type={'submit'}
                                    disabled={isPending || !form.isValid()}
                                    rightSection={
                                        isPending ? (
                                            <FontAwesomeIcon
                                                icon={faSpinnerThird}
                                                spin
                                            />
                                        ) : null
                                    }>
                                    {existing ? 'Update' : 'Create'}
                                </Button>
                            </Group>
                        </Stack>
                    </form>
                </Modal.Body>
            </Modal.Content>
        </Modal.Root>
    );
};
export default EditLinkModal;
